<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useCommercialDashboardStore } from '@/stores/useCommercialDashboardStore'
import { formatAmount, memberFullName } from './dashboardFormat'

const store = useCommercialDashboardStore()
const { teamData } = storeToRefs(store)
</script>

<template>
  <div v-if="teamData" class="card bg-base-100 shadow">
    <div class="card-body p-0">
      <!-- Tableau (tablette / desktop ≥ md) -->
      <div class="hidden overflow-x-auto md:block">
        <table class="table table-zebra">
          <thead>
            <tr>
              <th>Commercial</th>
              <th class="text-right">Clients</th>
              <th class="text-right">CA facturé</th>
              <th class="text-right">Impayés</th>
              <th class="text-right">Renouvellements</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="member in teamData.members" :key="member.user.uuid">
              <td>
                <div class="font-semibold">{{ memberFullName(member.user) }}</div>
                <div class="text-xs text-base-content/50">{{ member.user.bu ?? '—' }}</div>
              </td>
              <td class="text-right">{{ member.stats.kpis.totalClients }}</td>
              <td class="text-right font-medium">{{ formatAmount(member.stats.kpis.totalInvoiced) }}</td>
              <td class="text-right">
                <span
                  :class="member.stats.kpis.totalOutstanding > 0 ? 'text-error font-semibold' : 'text-base-content/40'"
                >
                  {{ member.stats.kpis.totalOutstanding > 0 ? formatAmount(member.stats.kpis.totalOutstanding) : '—' }}
                </span>
              </td>
              <td class="text-right">
                <span v-if="member.stats.renewals.length > 0" class="badge badge-error badge-sm">
                  {{ member.stats.renewals.length }}
                </span>
                <span v-else class="text-base-content/40">—</span>
              </td>
              <td>
                <button
                  class="btn btn-ghost btn-xs"
                  @click="store.fetchMemberDashboard(member.user.uuid)"
                >
                  Voir
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Cartes (mobile < md) — tableau dense illisible sur petit écran -->
      <ul role="list" class="flex flex-col gap-2 p-4 md:hidden">
        <li
          v-for="member in teamData.members"
          :key="member.user.uuid"
          class="rounded-lg border border-base-200 p-3"
        >
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <div class="font-semibold">{{ memberFullName(member.user) }}</div>
              <div class="text-xs text-base-content/50">{{ member.user.bu ?? '—' }}</div>
            </div>
            <button
              class="btn btn-ghost btn-sm shrink-0"
              @click="store.fetchMemberDashboard(member.user.uuid)"
            >
              Voir
            </button>
          </div>
          <dl class="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
            <div class="flex flex-col">
              <dt class="text-xs uppercase tracking-wide text-base-content/70">Clients</dt>
              <dd class="tabular-nums">{{ member.stats.kpis.totalClients }}</dd>
            </div>
            <div class="flex flex-col">
              <dt class="text-xs uppercase tracking-wide text-base-content/70">CA facturé</dt>
              <dd class="font-medium tabular-nums">{{ formatAmount(member.stats.kpis.totalInvoiced) }}</dd>
            </div>
            <div class="flex flex-col">
              <dt class="text-xs uppercase tracking-wide text-base-content/70">Impayés</dt>
              <dd
                class="tabular-nums"
                :class="member.stats.kpis.totalOutstanding > 0 ? 'font-semibold text-error' : 'text-base-content/40'"
              >
                {{ member.stats.kpis.totalOutstanding > 0 ? formatAmount(member.stats.kpis.totalOutstanding) : '—' }}
              </dd>
            </div>
            <div class="flex flex-col">
              <dt class="text-xs uppercase tracking-wide text-base-content/70">Renouvellements</dt>
              <dd>
                <span v-if="member.stats.renewals.length > 0" class="badge badge-error badge-sm">
                  {{ member.stats.renewals.length }}
                </span>
                <span v-else class="text-base-content/40">—</span>
              </dd>
            </div>
          </dl>
        </li>
      </ul>
    </div>
  </div>
</template>
